import type { RetiredCodeBehaviour } from '@myplayer/schema';
import { IconCheck, IconKey, IconPlus, IconSettings } from '@myplayer/ui';
import type { FormEvent, ReactElement } from 'react';
import {
  Button,
  Checkbox,
  Input,
  ModalDialog,
  Select,
  Switch,
} from './components/ui/primitives.js';
import type { PartnerApiScope } from './firebase.js';
import { WORKSPACE_LOCALE_OPTIONS } from './locale-region-options.js';
import { AVAILABLE_PARTNER_API_SCOPES } from './workspaces-firestore.js';

export function WorkspaceSettingsModal({
  open,
  workspaceId,
  workspaceName,
  canUpdateWorkspace,
  submitting,
  draftFallbackUrl,
  setDraftFallbackUrl,
  draftLocale,
  setDraftLocale,
  draftRetiredBehaviour,
  setDraftRetiredBehaviour,
  draftPrefixesInput,
  setDraftPrefixesInput,
  draftEnforcePrefix,
  setDraftEnforcePrefix,
  errorMessage,
  onClose,
  onSubmit,
}: {
  readonly open: boolean;
  readonly workspaceId: string;
  readonly workspaceName?: string;
  readonly canUpdateWorkspace: boolean;
  readonly submitting: boolean;
  readonly draftFallbackUrl: string;
  readonly setDraftFallbackUrl: (v: string) => void;
  readonly draftLocale: string;
  readonly setDraftLocale: (v: string) => void;
  readonly draftRetiredBehaviour: RetiredCodeBehaviour;
  readonly setDraftRetiredBehaviour: (v: RetiredCodeBehaviour) => void;
  readonly draftPrefixesInput: string;
  readonly setDraftPrefixesInput: (v: string) => void;
  readonly draftEnforcePrefix: boolean;
  readonly setDraftEnforcePrefix: (v: boolean) => void;
  readonly showAdvanced?: boolean;
  readonly setShowAdvanced?: (v: boolean) => void;
  readonly errorMessage: string | null;
  readonly onClose: () => void;
  readonly onSubmit: (e: FormEvent) => void;
}): ReactElement {
  const displayWorkspace = workspaceName ?? workspaceId;
  return (
    <ModalDialog
      open={open}
      title="Edit Workspace Defaults"
      subtitle={`Update fallback link, default language, and barcode rules for ${displayWorkspace}`}
      icon={<IconSettings />}
      presentation="center"
      maxWidthClassName="max-w-2xl"
      submitting={submitting}
      onClose={onClose}
      data-testid="workspace-settings-modal"
      footer={
        <>
          <Button
            type="button"
            variant="secondary"
            size="sm"
            disabled={submitting}
            onClick={onClose}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            form="workspace-settings-form"
            variant="primary"
            size="sm"
            disabled={submitting}
            aria-label="Save Configuration"
          >
            <IconCheck />
            <span>Save</span>
          </Button>
        </>
      }
    >
      <form id="workspace-settings-form" noValidate onSubmit={onSubmit} className="space-y-4">
        <div className="grid items-start gap-4 md:grid-cols-2">
          <div className="flex flex-col">
            <label
              htmlFor="ws-default-fallback-url"
              className="mb-1.5 block text-[12px] font-medium leading-4 text-fg-secondary"
            >
              Default Fallback URL
            </label>
            <Input
              id="ws-default-fallback-url"
              type="url"
              value={draftFallbackUrl}
              onChange={(e) => setDraftFallbackUrl(e.target.value)}
              disabled={!canUpdateWorkspace || submitting}
            />
            <p className="mt-1.5 text-[11px] leading-snug text-fg-secondary">
              Where scanners go when a product does not have its own link.
            </p>
          </div>
          <div className="flex flex-col">
            <label
              htmlFor="ws-default-locale"
              className="mb-1.5 block text-[12px] font-medium leading-4 text-fg-secondary"
            >
              Default Language
            </label>
            <Select
              id="ws-default-locale"
              aria-label="Default Locale (BCP-47)"
              value={draftLocale}
              onChange={(e) => setDraftLocale(e.target.value)}
              disabled={!canUpdateWorkspace || submitting}
              searchPlaceholder="Search locales..."
            >
              {!WORKSPACE_LOCALE_OPTIONS.some((opt) => opt.value === draftLocale) &&
                draftLocale.trim().length > 0 && <option value={draftLocale}>{draftLocale}</option>}
              {WORKSPACE_LOCALE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </Select>
            <p className="mt-1.5 text-[11px] leading-snug text-fg-secondary">
              Used when a scanner&apos;s language does not match a specific link.
            </p>
          </div>
        </div>
        <div
          data-testid="workspace-advanced-section"
          className="space-y-4 border-t border-subtle pt-4"
        >
          <div className="grid items-start gap-4 md:grid-cols-2">
            <div className="flex flex-col">
              <label
                htmlFor="ws-retired-behaviour"
                className="mb-1.5 block text-[12px] font-medium leading-4 text-fg-secondary"
              >
                Retired Identifier Behaviour
              </label>
              <Select
                id="ws-retired-behaviour"
                value={draftRetiredBehaviour}
                onChange={(e) => setDraftRetiredBehaviour(e.target.value as RetiredCodeBehaviour)}
                disabled={!canUpdateWorkspace || submitting}
              >
                <option value="GONE_410">Show page not found (410)</option>
                <option value="REDIRECT_FALLBACK">Send to default fallback web address</option>
              </Select>
              <p className="mt-1.5 text-[11px] leading-snug text-fg-secondary">
                How scanners handle discontinued products.
              </p>
            </div>
            <div className="flex flex-col">
              <label
                htmlFor="ws-gs1-prefixes"
                className="mb-1.5 block text-[12px] font-medium leading-4 text-fg-secondary"
              >
                GS1 Company Prefixes
              </label>
              <Input
                id="ws-gs1-prefixes"
                aria-label="GS1 Company Prefixes (comma-separated)"
                type="text"
                value={draftPrefixesInput}
                onChange={(e) => setDraftPrefixesInput(e.target.value)}
                placeholder="5012345, 9506000"
                disabled={!canUpdateWorkspace || submitting}
                className="tabular-nums"
              />
              <p className="mt-1.5 text-[11px] leading-snug text-fg-secondary">
                Comma-separated prefixes licensed to your organisation.
              </p>
            </div>
          </div>
          <Switch
            id="ws-enforce-prefix"
            checked={draftEnforcePrefix}
            onChange={(e) => setDraftEnforcePrefix(e.target.checked)}
            disabled={!canUpdateWorkspace || submitting}
            label="Enforce GS1 Company Prefix ownership on identifier promotion"
          />
        </div>
        {errorMessage !== null && (
          <p
            role="alert"
            data-testid="workspace-settings-error"
            className="rounded-control border border-status-error-border bg-status-error-bg p-2.5 text-[12px] font-medium text-status-error"
          >
            {errorMessage}
          </p>
        )}
      </form>
    </ModalDialog>
  );
}

export function CreateApiKeyModal({
  open,
  workspaceId,
  workspaceName,
  availableWorkspaces,
  onWorkspaceChange,
  keyName,
  setKeyName,
  rateLimitRpm,
  setRateLimitRpm,
  selectedScopes,
  toggleScope,
  submitting,
  errorMessage,
  onClose,
  onSubmit,
}: {
  readonly open: boolean;
  readonly workspaceId: string;
  readonly workspaceName?: string;
  readonly availableWorkspaces?: readonly { readonly workspaceId: string; readonly name: string }[];
  readonly onWorkspaceChange?: (val: string) => void;
  readonly keyName: string;
  readonly setKeyName: (v: string) => void;
  readonly rateLimitRpm: string;
  readonly setRateLimitRpm: (v: string) => void;
  readonly selectedScopes: readonly PartnerApiScope[];
  readonly toggleScope: (s: PartnerApiScope) => void;
  readonly showAdvanced?: boolean;
  readonly setShowAdvanced?: (v: boolean) => void;
  readonly submitting: boolean;
  readonly errorMessage: string | null;
  readonly onClose: () => void;
  readonly onSubmit: (e: FormEvent) => void;
}): ReactElement {
  const displayWorkspace = workspaceName ?? workspaceId;
  const workspaces = availableWorkspaces ?? [];
  return (
    <ModalDialog
      open={open}
      title="Create Partner API Key"
      subtitle={`Generate a scoped API key for ${displayWorkspace}`}
      icon={<IconKey />}
      presentation="center"
      maxWidthClassName="max-w-2xl"
      submitting={submitting}
      onClose={onClose}
      data-testid="create-api-key-modal"
      footer={
        <>
          <Button
            type="button"
            variant="secondary"
            size="sm"
            disabled={submitting}
            onClick={onClose}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            form="create-api-key-form"
            variant="primary"
            size="sm"
            disabled={submitting}
            className="gap-1.5"
          >
            <IconPlus />
            <span>Create API Key</span>
          </Button>
        </>
      }
    >
      <form
        id="create-api-key-form"
        aria-label="Create API key"
        noValidate
        onSubmit={onSubmit}
        className="space-y-4"
      >
        {workspaces.length > 1 && onWorkspaceChange && (
          <div>
            <label
              htmlFor="api-key-workspace"
              className="mb-1.5 block text-[12px] font-medium text-fg-secondary"
            >
              Workspace
            </label>
            <Select
              id="api-key-workspace"
              aria-label="Workspace"
              data-testid="api-key-workspace-select"
              value={workspaceId}
              onChange={(e) => onWorkspaceChange(e.target.value)}
              disabled={submitting}
            >
              {workspaces.map((ws) => (
                <option key={ws.workspaceId} value={ws.workspaceId}>
                  {ws.name}
                </option>
              ))}
            </Select>
          </div>
        )}
        <div>
          <label
            htmlFor="api-key-name"
            className="mb-1.5 block text-[12px] font-medium text-fg-secondary"
          >
            Key Name
          </label>
          <Input
            id="api-key-name"
            type="text"
            value={keyName}
            onChange={(e) => setKeyName(e.target.value)}
            placeholder="ERP Sync Key"
            disabled={submitting}
          />
        </div>
        <fieldset className="space-y-2">
          <legend className="text-[12px] font-medium text-fg-secondary">Permissions</legend>
          <div className="flex flex-wrap gap-2.5 pt-1">
            {AVAILABLE_PARTNER_API_SCOPES.map((scope) => (
              <Checkbox
                key={scope.value}
                variant="chip"
                checked={selectedScopes.includes(scope.value)}
                onChange={() => toggleScope(scope.value)}
                disabled={submitting}
                label={scope.label}
              />
            ))}
          </div>
        </fieldset>
        <div data-testid="api-key-advanced-section" className="border-t border-subtle pt-4">
          <label
            htmlFor="api-key-rpm"
            className="mb-1.5 block text-[12px] font-medium text-fg-secondary"
          >
            Rate Limit (requests per minute)
          </label>
          <Input
            id="api-key-rpm"
            type="number"
            min={10}
            max={60000}
            value={rateLimitRpm}
            onChange={(e) => setRateLimitRpm(e.target.value)}
            disabled={submitting}
            className="tabular-nums"
          />
          <p className="mt-1 text-[11px] text-fg-secondary">
            Maximum requests per minute allowed for this key (default 600).
          </p>
        </div>
        {errorMessage ? (
          <p
            role="alert"
            data-testid="create-api-key-error"
            className="rounded-control border border-status-error-border bg-status-error-bg p-3 text-[12px] font-medium text-status-error"
          >
            {errorMessage}
          </p>
        ) : null}
      </form>
    </ModalDialog>
  );
}
